import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronRight, Plus, Building2, Box, LayoutGrid, Rows3, DoorOpen, Circle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/AppShell";
import { LocationDialog } from "@/components/LocationDialog";
import { NONE, useLocations } from "@/lib/hooks";
import { useWs } from "@/context/Auth";
import { useI18n } from "@/i18n/I18n";
import { cn } from "@/lib/utils";

export const KIND_ICON = { site: Building2, room: DoorOpen, zone: LayoutGrid, shelf: Rows3, box: Box, other: Circle };

const TreeRow = ({ loc, depth, hasKids, open, onToggle }) => {
  const { t } = useI18n();
  const Icon = KIND_ICON[loc.kind] || Circle;
  return (
    <li className={cn("flex items-center gap-2 rounded-lg pe-2 hover:bg-slate-50 transition-colors", depth === 0 && "py-1")} style={{ paddingInlineStart: depth * 20 }}>
      <button data-testid={`tree-toggle-${loc.id}`} onClick={onToggle} className={cn("p-2 rounded-md", !hasKids && "invisible")} aria-label="toggle">
        <ChevronRight className={cn("w-4 h-4 text-slate-400 transition-transform rtl:rotate-180", open && "rotate-90 rtl:rotate-90")} />
      </button>
      <Link to={`/locations/${loc.id}`} data-testid={`tree-node-${loc.id}`} className="flex flex-1 items-center gap-3 py-2.5 min-w-0">
        <Icon className={cn("w-4 h-4 shrink-0", depth === 0 ? "text-blue-600" : "text-slate-400")} />
        <span className={cn("truncate", depth === 0 ? "font-bold text-slate-900" : "text-slate-700")}>{loc.name}</span>
        <span className="text-xs text-slate-400 shrink-0">{t(`kind.${loc.kind}`)}</span>
      </Link>
      <span className="font-mono text-xs text-slate-500 shrink-0">{loc.item_count || ""}</span>
    </li>
  );
};

export default function Locations() {
  const { t } = useI18n();
  const { canWrite } = useWs();
  const [params] = useSearchParams();
  const [open, setOpen] = useState(params.get("new") === "1");
  const { data: locs = NONE, isLoading } = useLocations();
  const childrenOf = useMemo(() => locs.reduce((acc, l) => { const k = l.parent_id || "root"; acc[k] = acc[k] || []; acc[k].push(l); return acc; }, {}), [locs]);
  const roots = childrenOf.root || [];
  const [expanded, setExpanded] = useState({});
  const isOpen = (l) => expanded[l.id] ?? !l.parent_id;
  const rows = [];
  const stack = [...roots].reverse().map((l) => [l, 0]);
  while (stack.length) {
    const [l, d] = stack.pop();
    const kids = childrenOf[l.id] || [];
    rows.push({ l, d, hasKids: kids.length > 0 });
    if (isOpen(l)) [...kids].reverse().forEach((k) => stack.push([k, d + 1]));
  }

  return (
    <div>
      <PageHeader title={t("loc.title")} actions={canWrite && (
        <Button data-testid="add-place-button" onClick={() => setOpen(true)} className="h-11 gap-2"><Plus className="w-4 h-4" />{t("loc.addPlace")}</Button>)} />
      {isLoading ? <p className="text-slate-500">{t("common.loading")}</p> : roots.length === 0 ? (
        <p data-testid="locations-empty" className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-slate-500">{t("loc.empty")}</p>
      ) : (
        <ul data-testid="location-tree" className="rounded-2xl border border-slate-200 bg-white p-2 sm:p-3 space-y-1 fade-up">
          {rows.map(({ l, d, hasKids }) => <TreeRow key={l.id} loc={l} depth={d} hasKids={hasKids} open={isOpen(l)} onToggle={() => setExpanded({ ...expanded, [l.id]: !isOpen(l) })} />)}
        </ul>
      )}
      <LocationDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
